import { useEffect, useRef, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../contexts/ToastContext';
import { track } from '../utils/analytics';
import { trackSnapshotResults } from '../utils/cloudBackup';
import { formatDjImportSummary } from '../utils/djImportSummary';
import { getImportExclusions } from '../utils/djImportSelection';
import { getAutoSyncLastRuns, markAutoSyncRun } from '../utils/djAutoSync';
import { hydrateAutoSyncFromStorage } from '../redux/slices/djAutoSyncSlice';
import { requestSync } from '../redux/slices/djLibraryChangesSlice';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { errMsg } from '../redux/utils/errMsg';
import {
    setRekordboxSidebarOpen, setImporting as setRekordboxImporting,
    setLastSyncTime as setRekordboxLastSyncTime, setSyncStatus as setRekordboxSyncStatus,
    setRekordboxPlaylists,
} from '../redux/slices/rekordboxSlice';
import {
    setSeratoSidebarOpen, setSeratoImporting, setSeratoLastSyncTime, setSeratoSyncStatus, setSeratoPlaylists,
} from '../redux/slices/seratoSlice';
import {
    setTraktorSidebarOpen, setTraktorImporting, setTraktorLastSyncTime, setTraktorSyncStatus, setTraktorPlaylists,
} from '../redux/slices/traktorSlice';
import {
    setVirtualDJSidebarOpen, setVirtualDJImporting, setVirtualDJLastSyncTime, setVirtualDJSyncStatus, setVirtualDJPlaylists,
} from '../redux/slices/virtualdjSlice';
import rekordboxLogo from '../assets/rekordbox-logo.png';
import seratoLogo from '../assets/serato-logo-10px.png';
import traktorLogo from '../assets/traktor-logo.png';
import virtualdjLogo from '../assets/virtual-dj-logo.png';

/**
 * Runs imports automatically when the DJ-library watcher reports changes and
 * the user has Auto-sync ON for that app. Renders nothing; mounted for the
 * whole session next to DjLibraryChangeListener.
 *
 * Policy (the guardrails matter more than the automation):
 * - Only diffs the user could one-click today are auto-imported. A diff that
 *   contains never-imported playlists is NOT auto-synced unless the per-app
 *   "include new" pref is on — the change listener shows a "Review" card
 *   instead, so hundreds of new crates can't flood in unreviewed.
 * - The import goes through the SAME IPC as a manual sync (exclusions apply,
 *   watcher re-baselines, cloud push happens) — auto and manual can't drift.
 * - Per-app min-interval so a DJ app that's open and writing constantly
 *   doesn't trigger import churn; one import at a time globally (they're
 *   heavy: per-track file I/O in the main process).
 * - Quiet receipts: a card toast only when something actually changed and the
 *   window is focused; failures retry once, then surface a "Sync now" card.
 */
const MIN_INTERVAL_MS = 5 * 60 * 1000;
const RETRY_DELAY_MS = 30 * 1000;
const MAX_ATTEMPTS = 2;
// No auto-import in the first stretch after mount: launch/login already
// stacks the watcher's boot pass, playlist fullSync, and feed/tier fetches on
// the main process — an import on top of that froze the window (2026-08-25).
// The pending diff keeps; the import just starts after the app has settled.
const STARTUP_GRACE_MS = 90 * 1000;

const APPS = {
    rekordbox: {
        label: 'Rekordbox', noun: 'playlist', logo: rekordboxLogo,
        open: setRekordboxSidebarOpen, setImporting: setRekordboxImporting,
        setLastSyncTime: setRekordboxLastSyncTime, setSyncStatus: setRekordboxSyncStatus,
        setPlaylists: setRekordboxPlaylists,
        run: async (options) => {
            // Same source the sidebar uses; fall back to detection when this
            // session never opened the sidebar.
            let p = localStorage.getItem('rekordbox.path');
            if (!p || p === 'null') {
                const r = await window.electron.rekordbox.checkXmlExists();
                if (!r?.exists) throw new Error('Rekordbox library not found');
                p = r.path;
            }
            return window.electron.rekordbox.importPlaylists(p, options);
        },
    },
    serato: {
        label: 'Serato', noun: 'crate', logo: seratoLogo,
        open: setSeratoSidebarOpen, setImporting: setSeratoImporting,
        setLastSyncTime: setSeratoLastSyncTime, setSyncStatus: setSeratoSyncStatus,
        setPlaylists: setSeratoPlaylists,
        run: (options) => window.electron.serato.importPlaylists(options),
    },
    traktor: {
        label: 'Traktor', noun: 'playlist', logo: traktorLogo,
        open: setTraktorSidebarOpen, setImporting: setTraktorImporting,
        setLastSyncTime: setTraktorLastSyncTime, setSyncStatus: setTraktorSyncStatus,
        setPlaylists: setTraktorPlaylists,
        run: (options) => window.electron.traktor.importPlaylists(options),
    },
    virtualdj: {
        label: 'Virtual DJ', noun: 'playlist', logo: virtualdjLogo,
        open: setVirtualDJSidebarOpen, setImporting: setVirtualDJImporting,
        setLastSyncTime: setVirtualDJLastSyncTime, setSyncStatus: setVirtualDJSyncStatus,
        setPlaylists: setVirtualDJPlaylists,
        run: (options) => window.electron.virtualdj.importPlaylists(options),
    },
};

export default function AutoSyncManager() {
    const dispatch = useDispatch();
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;

    const pending = useSelector((s) => s.djLibraryChanges?.pending);
    const prefs = useSelector((s) => s.djAutoSync.prefs);
    const hydrated = useSelector((s) => s.djAutoSync.hydrated);
    const userId = useSelector((s) => s.auth?.user?.id) || null;
    const importingRekordbox = useSelector((s) => s.rekordbox.importing);
    const importingSerato = useSelector((s) => s.serato.importing);
    const importingTraktor = useSelector((s) => s.traktor.importing);
    const importingVirtualdj = useSelector((s) => s.virtualdj.importing);
    const sidebarOpenByApp = useSelector((s) => ({
        rekordbox: s.rekordbox.sidebarOpen, serato: s.serato.sidebarOpen,
        traktor: s.traktor.sidebarOpen, virtualdj: s.virtualdj.sidebarOpen,
    }), (a, b) => JSON.stringify(a) === JSON.stringify(b));

    const [tick, bump] = useReducer((x) => x + 1, 0);
    const lastRunAtRef = useRef({});     // app -> ts of last auto-import start (persisted per user)
    const failuresRef = useRef({});      // app -> { at: change.at, count }
    const busyRef = useRef(false);       // one import at a time, they're heavy
    const timersRef = useRef({});
    const mountedAtRef = useRef(Date.now());

    // Prefs are keyed per auth bucket — reload on boot and account switch.
    // Last-run timestamps come from the same bucket so the MIN_INTERVAL
    // throttle survives a relaunch.
    useEffect(() => {
        dispatch(hydrateAutoSyncFromStorage());
        lastRunAtRef.current = getAutoSyncLastRuns(userId);
    }, [dispatch, userId]);

    useEffect(() => {
        if (!hydrated || !pending) return undefined;
        const importingByApp = {
            rekordbox: importingRekordbox, serato: importingSerato,
            traktor: importingTraktor, virtualdj: importingVirtualdj,
        };

        const runImport = async (app, change) => {
            const meta = APPS[app];
            busyRef.current = true;
            lastRunAtRef.current[app] = Date.now();
            markAutoSyncRun(app, userId);
            dispatch(meta.setImporting(true));
            try {
                const excludeKeys = getImportExclusions(app, userId);
                const result = await meta.run({ excludeKeys: excludeKeys.length ? excludeKeys : undefined });
                dispatch(meta.setLastSyncTime(new Date().toISOString()));
                dispatch(meta.setSyncStatus('synced'));
                dispatch(triggerPlaylistRefresh());
                // The sidebar caches its imported-playlist list (with counts);
                // stale after a background import. Only invalidate while it's
                // closed — it refetches on open.
                if (!sidebarOpenByApp[app]) dispatch(meta.setPlaylists([]));
                failuresRef.current[app] = null;
                track('dj_auto_sync_run', {
                    app,
                    imported: result.imported ?? 0,
                    updated: result.updated ?? 0,
                    tracks_created: result.tracksCreated ?? 0,
                    tracks_updated: result.tracksUpdated ?? 0,
                });
                // Piggyback a Cloud Backup snapshot pass on the sync we just
                // finished, INSIDE the same single-flight (busyRef is still
                // held) — the DJ app just wrote its library, so this is the
                // moment a snapshot is freshest. The engine's own 24h and
                // change gates make it a no-op most of the time, and the
                // main process refuses when backup is disabled/not GOAT.
                if (window.electron?.backup?.runSnapshot) {
                    try {
                        // `background`: the app's idea, not a click — the
                        // app-wide toast stays quiet unless something uploads.
                        const snap = await window.electron.backup.runSnapshot({ source: 'auto-sync', background: true });
                        // First backup on a machine emptier than the cloud —
                        // the engine held the upload; surface the decision
                        // sheet (ShrinkGuardPrompt shows it once per session).
                        const guard = snap?.results?.find((r) => r.status === 'needs_confirmation');
                        if (guard) {
                            window.dispatchEvent(new CustomEvent('mixgoat:backup-shrink-guard', { detail: guard.guard }));
                        } else if (snap?.results) {
                            trackSnapshotResults(snap.results, 'auto_sync');
                        }
                    } catch { /* backup must never break a sync */ }
                }
                const changed = (result.imported || 0) + (result.updated || 0)
                    + (result.tracksCreated || 0) + (result.tracksUpdated || 0) + (result.tracksRefilled || 0) > 0;
                if (changed && document.hasFocus()) {
                    toastRef.current.card({
                        artwork: meta.logo,
                        artworkKind: 'logo',
                        title: `Auto-synced from ${meta.label}`,
                        message: formatDjImportSummary(result, { app: meta.label, noun: meta.noun }),
                        action: { label: 'View', onClick: () => dispatch(meta.open(true)) },
                        duration: 6000,
                    });
                }
            } catch (err) {
                const prev = failuresRef.current[app];
                const count = prev && prev.at === change.at ? prev.count + 1 : 1;
                failuresRef.current[app] = { at: change.at, count };
                track('dj_auto_sync_failed', { app, attempt: count });
                if (count >= MAX_ATTEMPTS) {
                    // Hand back to the manual path — pending diff (and rail
                    // dot) are still there, this card is the shortcut.
                    toastRef.current.card({
                        artwork: meta.logo,
                        artworkKind: 'logo',
                        title: `Auto-sync from ${meta.label} hit a problem`,
                        message: errMsg(err),
                        action: {
                            label: 'Sync now',
                            onClick: () => { dispatch(requestSync(app)); dispatch(meta.open(true)); },
                        },
                        duration: 12000,
                    });
                } else {
                    lastRunAtRef.current[app] = 0; // allow the retry through the interval gate
                    setTimeout(bump, RETRY_DELAY_MS);
                }
            } finally {
                dispatch(meta.setImporting(false));
                busyRef.current = false;
            }
        };

        for (const app of Object.keys(pending)) {
            const change = pending[app];
            if (!change?.diff || !APPS[app]) continue;
            const p = prefs[app];
            if (!p?.enabled) continue;
            // Never-imported playlists in the diff → review, not automation
            // (unless the user opted into "include new" for this app).
            if (change.diff.newPlaylists?.length && !p.includeNew) continue;
            const fail = failuresRef.current[app];
            if (fail && fail.at === change.at && fail.count >= MAX_ATTEMPTS) continue;
            if (busyRef.current || importingByApp[app]) continue; // re-evaluated when flags flip
            const wait = Math.max(
                (lastRunAtRef.current[app] || 0) + MIN_INTERVAL_MS - Date.now(),
                mountedAtRef.current + STARTUP_GRACE_MS - Date.now(),
            );
            if (wait > 0) {
                clearTimeout(timersRef.current[app]);
                timersRef.current[app] = setTimeout(bump, wait + 100);
                continue;
            }
            runImport(app, change);
            break; // busyRef gates the rest; they run as flags flip
        }
        return undefined;
        // `tick` re-arms the evaluation when a wait timer or retry fires.
    }, [hydrated, pending, prefs, userId, importingRekordbox, importingSerato,
        importingTraktor, importingVirtualdj, sidebarOpenByApp, dispatch, tick]);

    useEffect(() => () => {
        for (const t of Object.values(timersRef.current)) clearTimeout(t);
    }, []);

    return null;
}
